// src/admin/hooks/useCollectionsCase.ts
// Assembles everything the collections detail screen needs for a loan:
// the queue-view row (for days_overdue + outstanding + last-contact
// signal), the loan row (principal/interest/fee breakdown), the
// borrower's credit tier, the full contact timeline, and the repayment
// schedule.
//
// The gen-types script emits `Relationships: []` for every table (no FK
// metadata on either kyc_submissions or collection_contacts), so the
// embedded-select strategy that works in the React Query docs won't
// type-check here. We fall back to five parallel round-trips plus one
// follow-up to resolve the unique `contacted_by` admin ids into display
// names — cheaper than it sounds because all five can share the
// connection pool and the follow-up only fires if contacts exist.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';
import type { Ngwee } from '@/lib/types';

type LoanStatus = Database['public']['Enums']['loan_status'];
type CreditTier = Database['public']['Enums']['credit_tier'];
type InstallmentStatus = Database['public']['Enums']['loan_installment_status'];
type CollectionChannel = Database['public']['Enums']['collection_channel'];
type CollectionOutcome = Database['public']['Enums']['collection_outcome'];

export interface CollectionsCaseLoan {
  id: string;
  loan_number: string;
  status: LoanStatus;
  principal_ngwee: Ngwee;
  interest_ngwee: Ngwee;
  fee_ngwee: Ngwee;
  total_repayable_ngwee: Ngwee | null;
  outstanding_ngwee: Ngwee;
  days_overdue: number;
  disbursed_at: string | null;
  due_date: string | null;
  last_payment_at: string | null;
  last_payment_amount_ngwee: Ngwee | null;
}

export interface CollectionsCaseBorrower {
  id: string;
  display_name: string | null;
  phone: string | null;
  credit_score: number | null;
  credit_tier: CreditTier | null;
}

export interface CollectionsContact {
  id: string;
  contacted_at: string;
  channel: CollectionChannel;
  outcome: CollectionOutcome;
  ptp_amount_ngwee: Ngwee | null;
  ptp_date: string | null;
  notes: string;
  contacted_by: string;
  agent_name: string | null;
}

export interface CollectionsScheduleRow {
  id: string;
  installment_no: number;
  due_date: string;
  scheduled_amount_ngwee: Ngwee;
  paid_amount_ngwee: Ngwee;
  status: InstallmentStatus;
}

export interface CollectionsCase {
  loan: CollectionsCaseLoan;
  borrower: CollectionsCaseBorrower;
  contacts: CollectionsContact[];
  schedule: CollectionsScheduleRow[];
}

export function useCollectionsCase(loanId: string | undefined) {
  return useQuery<CollectionsCase | null>({
    queryKey: ['collections-case', loanId],
    enabled: !!loanId,
    queryFn: async () => {
      if (!loanId) return null;
      const [queueRes, loanRes, contactsRes, scheduleRes] = await Promise.all([
        supabase
          .from('collections_queue')
          .select(
            'loan_id, loan_number, borrower_id, borrower_name, borrower_phone, status, days_overdue, outstanding_ngwee, last_payment_at, last_payment_amount_ngwee',
          )
          .eq('loan_id', loanId)
          .maybeSingle(),
        supabase
          .from('loans')
          .select(
            'id, loan_number, user_id, status, principal_ngwee, interest_ngwee, fee_ngwee, total_repayable_ngwee, disbursed_at, due_date',
          )
          .eq('id', loanId)
          .maybeSingle(),
        supabase
          .from('collection_contacts')
          .select('id, contacted_at, channel, outcome, ptp_amount_ngwee, ptp_date, notes, contacted_by')
          .eq('loan_id', loanId)
          .order('contacted_at', { ascending: false }),
        supabase
          .from('loan_repayment_schedule')
          .select('id, installment_no, due_date, scheduled_amount_ngwee, paid_amount_ngwee, status')
          .eq('loan_id', loanId)
          .order('installment_no', { ascending: true }),
      ]);
      if (queueRes.error) throw queueRes.error;
      if (loanRes.error) throw loanRes.error;
      if (contactsRes.error) throw contactsRes.error;
      if (scheduleRes.error) throw scheduleRes.error;
      if (!loanRes.data) return null;

      // Credit score + agent names: two follow-ups we only run once the
      // core rows have resolved. Credit score is by user_id; agent names
      // are batched by unique contacted_by ids.
      const contactsRaw = contactsRes.data ?? [];
      const agentIds = Array.from(new Set(contactsRaw.map((c) => c.contacted_by)));

      const [scoreRes, agentsRes] = await Promise.all([
        supabase
          .from('credit_scores')
          .select('current_score, current_tier')
          .eq('user_id', loanRes.data.user_id)
          .maybeSingle(),
        agentIds.length > 0
          ? supabase.from('admin_users').select('id, display_name').in('id', agentIds)
          : Promise.resolve({ data: [] as Array<{ id: string; display_name: string }>, error: null }),
      ]);
      if (scoreRes.error) throw scoreRes.error;
      if (agentsRes.error) throw agentsRes.error;

      const agentById = new Map<string, string>();
      for (const a of agentsRes.data ?? []) agentById.set(a.id, a.display_name);

      // The queue view row is optional — only populated for loans that
      // have aged past due_date. For on-time loans being viewed out of
      // curiosity, fall back to zeros so the screen still renders.
      const q = queueRes.data;
      const loan: CollectionsCaseLoan = {
        id: loanRes.data.id,
        loan_number: loanRes.data.loan_number,
        status: loanRes.data.status,
        principal_ngwee: loanRes.data.principal_ngwee as Ngwee,
        interest_ngwee: loanRes.data.interest_ngwee as Ngwee,
        fee_ngwee: loanRes.data.fee_ngwee as Ngwee,
        total_repayable_ngwee: loanRes.data.total_repayable_ngwee as Ngwee | null,
        outstanding_ngwee: (q?.outstanding_ngwee ?? 0) as Ngwee,
        days_overdue: q?.days_overdue ?? 0,
        disbursed_at: loanRes.data.disbursed_at,
        due_date: loanRes.data.due_date,
        last_payment_at: q?.last_payment_at ?? null,
        last_payment_amount_ngwee: (q?.last_payment_amount_ngwee ?? null) as Ngwee | null,
      };

      return {
        loan,
        borrower: {
          id: q?.borrower_id ?? loanRes.data.user_id,
          display_name: q?.borrower_name ?? null,
          phone: q?.borrower_phone ?? null,
          credit_score: scoreRes.data?.current_score ?? null,
          credit_tier: scoreRes.data?.current_tier ?? null,
        },
        contacts: contactsRaw.map((c) => ({
          id: c.id,
          contacted_at: c.contacted_at,
          channel: c.channel,
          outcome: c.outcome,
          ptp_amount_ngwee: c.ptp_amount_ngwee as Ngwee | null,
          ptp_date: c.ptp_date,
          notes: c.notes,
          contacted_by: c.contacted_by,
          agent_name: agentById.get(c.contacted_by) ?? null,
        })),
        schedule: (scheduleRes.data ?? []).map((s) => ({
          id: s.id,
          installment_no: s.installment_no,
          due_date: s.due_date,
          scheduled_amount_ngwee: s.scheduled_amount_ngwee as Ngwee,
          paid_amount_ngwee: s.paid_amount_ngwee as Ngwee,
          status: s.status,
        })),
      };
    },
  });
}
